/**
 * Lazy-loaded wrappers for sweetalert2 and toastr.
 *
 * The original implementation imported both libraries (and toastr's CSS) at
 * module scope, which dragged ~165 KiB of vendor JS into the landing-page
 * critical path. The landing page never opens a modal or toast, so the cost
 * was paid for content the visitor never sees.
 *
 * Here the libraries are loaded on first call and cached for subsequent ones.
 * Public API stays the same; functions now return Promises but callers that
 * use fire-and-forget keep working unchanged.
 */
import type { SweetAlertResult } from 'sweetalert2'

let _swalPromise: Promise<typeof import('sweetalert2').default> | null = null
const getSwal = () => {
  if (!_swalPromise) {
    _swalPromise = import('sweetalert2').then((m) => m.default)
  }
  return _swalPromise
}

let _toastrPromise: Promise<Toastr> | null = null
const getToastr = () => {
  if (!_toastrPromise) {
    _toastrPromise = (async () => {
      const [toastrMod] = await Promise.all([
        import('toastr'),
        import('toastr/build/toastr.min.css'),
      ])
      const t: Toastr = toastrMod.default
      t.options = {
        closeButton: true,
        progressBar: true,
        positionClass: 'toast-bottom-right',
        timeOut: 4000,
        extendedTimeOut: 1000,
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut',
      }
      return t
    })()
  }
  return _toastrPromise
}

export const toast = {
  success: async (message: string, title?: string) => {
    (await getToastr()).success(message, title)
  },
  error: async (message: string, title?: string) => {
    (await getToastr()).error(message, title)
  },
  warning: async (message: string, title?: string) => {
    (await getToastr()).warning(message, title)
  },
  info: async (message: string, title?: string) => {
    (await getToastr()).info(message, title)
  },
}

const buttonConfig = {
  confirmButtonColor: '#4BC232',
  cancelButtonColor: '#6b7280',
  confirmButtonText: 'OK',
  customClass: {
    confirmButton: 'swal2-styled swal2-confirm',
    cancelButton: 'swal2-styled swal2-cancel',
  },
}

export const alert = {
  success: async (title: string, text?: string): Promise<SweetAlertResult> => {
    const Swal = await getSwal()
    return Swal.fire({ icon: 'success', title, text, ...buttonConfig })
  },
  error: async (title: string, text?: string): Promise<SweetAlertResult> => {
    const Swal = await getSwal()
    return Swal.fire({ icon: 'error', title, text, ...buttonConfig })
  },
  warning: async (title: string, text?: string): Promise<SweetAlertResult> => {
    const Swal = await getSwal()
    return Swal.fire({ icon: 'warning', title, text, ...buttonConfig })
  },
  info: async (title: string, text?: string): Promise<SweetAlertResult> => {
    const Swal = await getSwal()
    return Swal.fire({ icon: 'info', title, text, ...buttonConfig })
  },
  confirm: async (title: string, text?: string): Promise<SweetAlertResult> => {
    const Swal = await getSwal()
    return Swal.fire({
      icon: 'question',
      title,
      text,
      showCancelButton: true,
      confirmButtonColor: '#4BC232',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'swal2-styled swal2-confirm',
        cancelButton: 'swal2-styled swal2-cancel',
      },
    })
  },
  loading: async (title: string = 'Loading...') => {
    const Swal = await getSwal()
    Swal.fire({
      title,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })
  },
  close: async () => {
    if (!_swalPromise) return
    const Swal = await _swalPromise
    Swal.close()
  },
}
